import { useCallback, useMemo, useState } from "react";
import { catchError, of, tap } from "rxjs";

import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import type { APILibraryVerticalResponseType } from "@/core/data_sources/models/APILibraryVertical";
import { AppStore } from "@/core/data_sources/store/app";
import { Badge, Button } from "@/core/ui/atoms";
import { DataTable, PageHeader } from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { CreateVerticalDialog } from "./components/CreateVerticalDialog";
import { EditVerticalDrawer } from "./components/EditVerticalDrawer";
import { LibraryVerticalsStore } from "./data_sources/store";
import { StatusBadge } from "../../components/molecules";
import { LibraryTemplatePacksStore } from "../template-packs/data_sources/store";
import { LibraryTemplatesStore } from "../templates/data_sources/store";

export function VerticalsPage() {
  const { state, hooks } = useStore(LibraryVerticalsStore.instance);
  const { state: templateState, hooks: templateHooks } = useStore(
    LibraryTemplatesStore.instance,
  );
  const { state: templatePackState, hooks: templatePackHooks } = useStore(
    LibraryTemplatePacksStore.instance,
  );
  const { setQuery } = hooks.useVerticalStoreService({
    page: 1,
    page_size: 10,
  });
  templateHooks.useTemplateStoreService({
    page: 1,
    page_size: 1000,
  });
  templatePackHooks.useTemplatePackStoreService({
    page: 1,
    page_size: 1000,
  });
  const { actions: appActions } = useStore(AppStore.instance);

  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const refresh = useCallback(() => {
    setQuery((prev) => ({ ...prev }));
  }, [setQuery]);
  const handleSaved = useCallback(() => {
    refresh();
    closeDialog();
    closeDrawer();
  }, [closeDialog, closeDrawer, refresh]);
  const openCreateVerticalDialog = useCallback(() => {
    appActions.dialog(
      "open",
      <DialogTemplate>
        <CreateVerticalDialog onCancel={closeDialog} onCreated={handleSaved} />
      </DialogTemplate>,
    );
  }, [appActions, closeDialog, handleSaved]);
  const openEditVerticalDrawer = useCallback(
    (vertical: APILibraryVerticalResponseType) => {
      appActions.rightDrawer(
        "open",
        520,
        <EditVerticalDrawer
          onClose={closeDrawer}
          onSaved={handleSaved}
          vertical={vertical}
        />,
      );
    },
    [appActions, closeDrawer, handleSaved],
  );
  const openDeleteVerticalDialog = useCallback(
    (vertical: APILibraryVerticalResponseType) => {
      appActions.dialog(
        "open",
        <DialogTemplate>
          <DeleteVerticalDialog
            onCancel={closeDialog}
            onDeleted={handleSaved}
            vertical={vertical}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, handleSaved],
  );
  const verticals = state?.list.map((vertical) => vertical.getData()) ?? [];
  const templates =
    templateState?.list.map((template) => template.getData()) ?? [];
  const templatePacks =
    templatePackState?.list.map((pack) => pack.getData()) ?? [];
  const columns = useMemo(
    () => [
      {
        header: "Code",
        key: "code",
        render: (vertical: APILibraryVerticalResponseType) => (
          <span className="font-mono text-xs text-ink-muted">
            {vertical.code}
          </span>
        ),
      },
      {
        header: "Name",
        key: "name",
        render: (vertical: APILibraryVerticalResponseType) => (
          <div className="font-extrabold text-ink">
            {vertical.name}{" "}
            {vertical.is_system ? <Badge tone="global">System</Badge> : null}
          </div>
        ),
      },
      {
        header: "Status",
        key: "status",
        render: (vertical: APILibraryVerticalResponseType) => (
          <StatusBadge status={vertical.status} />
        ),
      },
      {
        header: "Used by",
        key: "usedBy",
        render: (vertical: APILibraryVerticalResponseType) => (
          <span className="text-sm text-ink-muted">
            {getVerticalUseCount(vertical.name ?? "", templates, templatePacks)}{" "}
            items
          </span>
        ),
      },
      {
        className: "w-44",
        header: "",
        key: "actions",
        render: (vertical: APILibraryVerticalResponseType) => {
          const useCount = getVerticalUseCount(
            vertical.name ?? "",
            templates,
            templatePacks,
          );

          return vertical.is_system ? (
            <span className="text-xs text-ink-subtle">-</span>
          ) : (
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => openEditVerticalDrawer(vertical)}
                size="sm"
              >
                Edit
              </Button>
              <Button
                disabled={useCount > 0}
                onClick={() => openDeleteVerticalDialog(vertical)}
                size="sm"
                variant="danger"
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [
      openDeleteVerticalDialog,
      openEditVerticalDrawer,
      templatePacks,
      templates,
    ],
  );

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={openCreateVerticalDialog} variant="primary">
            Create Vertical
          </Button>
        }
        breadcrumb={["Library", "Verticals"]}
        subtitle="Global vertical catalog. General is fallback and cannot be removed."
        title="Verticals"
      />
      <DataTable
        columns={columns}
        rowKey={(vertical) => vertical.code ?? String(vertical.id ?? "")}
        rows={verticals}
      />
    </>
  );
}

function DeleteVerticalDialog({
  onCancel,
  onDeleted,
  vertical,
}: {
  readonly onCancel: () => void;
  readonly onDeleted: () => void;
  readonly vertical: APILibraryVerticalResponseType;
}) {
  const { actions } = useStore(LibraryVerticalsStore.instance);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = useCallback(() => {
    if (!vertical.id) {
      setError("Vertical id is required.");
      return;
    }
    setDeleting(true);
    setError("");
    actions
      .deleteVertical(vertical.id)
      .pipe(
        tap(() => {
          onDeleted();
        }),
        catchError((requestError) => {
          setError(
            requestError instanceof ErrorResponse
              ? requestError.error.message
              : requestError instanceof Error
                ? requestError.message
                : "Unable to delete vertical.",
          );
          return of(null);
        }),
        tap(() => setDeleting(false)),
      )
      .subscribe();
  }, [actions, onDeleted, vertical.id]);

  return (
    <section className="w-md max-w-full">
      <div className="border-b border-border px-6 pb-3 pt-5">
        <h2 className="text-[17px] font-semibold text-ink">Delete vertical</h2>
        <p className="mt-1 text-[13px] text-ink-subtle">
          This removes <strong>{vertical.name}</strong> from the global Library
          catalog.
        </p>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-ink-muted">
          This action is only available when no templates or packs use the
          vertical.
        </p>
        {error ? <p className="mt-3 text-xs text-critical">{error}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t border-border bg-panel-muted px-6 py-3.5">
        <Button disabled={deleting} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={deleting} onClick={handleDelete} variant="danger">
          {deleting ? "Deleting..." : "Delete vertical"}
        </Button>
      </div>
    </section>
  );
}

function getVerticalUseCount(
  vertical: string,
  templates: readonly APILibraryTemplateResponseType[],
  templatePacks: readonly APILibraryTemplatePackResponseType[],
) {
  const templateCount = templates.filter((template) =>
    template.verticals?.some((item) => item.name === vertical),
  ).length;
  const packCount = templatePacks.filter((pack) =>
    pack.verticals?.some((item) => item.name === vertical),
  ).length;

  return templateCount + packCount;
}
